import React, { useCallback, useEffect, useState } from 'react'
import { font, radius, sidebar } from '../design/tokens'
import {
  PushState,
  disablePush,
  enablePush,
  getPushStatus,
  sendTestPush,
} from '../services/push'

/**
 * The one control for browser notifications, in the sidebar footer.
 *
 * It lives beside the approval badge rather than on a settings screen because
 * of what it is for: the badge is what tells you work arrived overnight, and
 * this is how you find out about it without coming to look. A notification
 * feature nobody discovers is a notification feature nobody has.
 *
 * It renders nothing at all when there is nothing to offer — a browser that
 * cannot push, or a deployment with no keypair. An "unavailable" row would be
 * a permanent piece of furniture saying no.
 */
const PushToggle: React.FC = () => {
  const [state, setState] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getPushStatus()
      .then(status => { if (!cancelled) setState(status.state) })
      .catch(() => { if (!cancelled) setState('unavailable') })
    return () => { cancelled = true }
  }, [])

  const flash = useCallback((message: string) => {
    setSaid(message)
    window.setTimeout(() => setSaid(null), 4000)
  }, [])

  // Nothing to offer: say nothing rather than keeping a row that says no.
  if (state === null || state === 'unsupported' || state === 'unavailable') return null

  // This lives in the sidebar footer, which is the app's one dark surface —
  // hence the sidebar palette rather than the page one.
  const rowStyle = {
    width: '100%',
    padding: '0.375rem 0.5rem',
    border: `1px solid ${sidebar.borderStrong}`,
    borderRadius: radius.md,
    backgroundColor: 'transparent',
    color: sidebar.text,
    cursor: busy ? 'default' : 'pointer',
    fontSize: font.size.xs,
    fontFamily: 'inherit',
    textAlign: 'left' as const,
  }

  if (state === 'denied') {
    // Permission cannot be asked for again from script once refused, so the
    // only honest thing to say is where the switch now lives.
    return (
      <p style={{ fontSize: font.size.xs, color: sidebar.textMuted, margin: '0 0 0.5rem', padding: '0 0.125rem' }}>
        通知はブラウザ側でブロックされています。アドレスバーのサイト設定から許可できます。
      </p>
    )
  }

  if (state === 'enabled') {
    return (
      <div style={{ marginBottom: '0.5rem' }}>
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <button
            type="button"
            disabled={busy}
            style={{ ...rowStyle, flex: 1 }}
            onClick={async () => {
              setBusy(true)
              try {
                flash(await sendTestPush())
              } catch {
                flash('テスト通知を送れませんでした。')
              } finally {
                setBusy(false)
              }
            }}
          >
            通知をテスト
          </button>
          <button
            type="button"
            disabled={busy}
            aria-label="通知を止める"
            title="通知を止める"
            style={{ ...rowStyle, width: '32px', textAlign: 'center' }}
            onClick={async () => {
              setBusy(true)
              try {
                setState((await disablePush()).state)
              } finally {
                setBusy(false)
              }
            }}
          >
            ×
          </button>
        </div>
        {said && (
          <p style={{ fontSize: font.size.xs, color: sidebar.textMuted, margin: '0.25rem 0 0', padding: '0 0.125rem' }}>
            {said}
          </p>
        )}
      </div>
    )
  }

  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <button
        type="button"
        disabled={busy}
        style={rowStyle}
        onClick={async () => {
          setBusy(true)
          try {
            const status = await enablePush()
            setState(status.state)
            if (status.state === 'denied') flash('ブラウザで通知が拒否されました。')
            else if (status.state !== 'enabled') flash('通知を有効にできませんでした。')
          } catch {
            flash('通知を有効にできませんでした。')
          } finally {
            setBusy(false)
          }
        }}
      >
        {busy ? '設定中…' : '🔔 自動実行の結果を通知で受け取る'}
      </button>
      {said && (
        <p style={{ fontSize: font.size.xs, color: sidebar.textMuted, margin: '0.25rem 0 0', padding: '0 0.125rem' }}>
          {said}
        </p>
      )}
    </div>
  )
}

export default PushToggle
